import { createFileRoute, redirect } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { OverviewPage } from '@/features/air/overview/components/page'
import { OverviewSkeleton } from '@/features/air/overview/components/skeletons'
import { overviewQueries } from '@/features/air/overview/queries'
import { overviewSearchSchema } from '@/features/air/overview/search-schema'
import { resolveWindow } from '@/features/air/overview/window'
import {
	rememberSelection,
	restoredSearch,
} from '@/features/air/overview/window-preference'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { integrationQueries } from '@/features/integrations/queries'

import { getCurrentTenant } from '@/shared/auth/store'

export const Route = createFileRoute('/_dashboard/air/_layout/overview')({
	validateSearch: zodValidator(overviewSearchSchema),

	loaderDeps: ({ search }) => ({
		preset: search.preset,
		from: search.from,
		to: search.to,
	}),

	/**
	 * Put the viewer's remembered preset back on a bare `/air/overview` — the URL
	 * the tab strip links to, which is why leaving the page and returning used to
	 * snap back to the default. `restoredSearch` owns when that applies.
	 *
	 * Here rather than in the component because `beforeLoad` precedes the loader:
	 * the prefetch below then warms the window the page will actually read,
	 * instead of the default it is about to leave. It replaces rather than
	 * pushes, so the bare URL is not an extra back-button stop on the way in.
	 */
	beforeLoad: ({ search }) => {
		const restored = restoredSearch(search)
		if (restored) {
			throw redirect({ to: '/air/overview', search: restored, replace: true })
		}
	},

	loader: ({ context: { queryClient }, deps }) => {
		const tenant = getCurrentTenant()
		if (!tenant) return

		// The loader resolves the same minute-floored window the page will, so the
		// prefetch warms the key the page then reads. A render that straddles a
		// minute boundary warms one key and reads the next — a wasted prefetch on
		// the rare navigation, never a stale figure.
		queryClient.prefetchQuery(
			overviewQueries.dashboard(tenant, resolveWindow(deps)),
		)

		// The integration catalog names the breakdown's Integration tab, which
		// suspends on it. Warmed here rather than fetched on the tab click, so
		// opening that tab is instant like the other three — the tab strip's whole
		// promise. It is not awaited: a slow catalog must not hold up the page, and
		// the tab has its own suspense fallback for the case where it is still in
		// flight.
		queryClient.prefetchQuery(integrationQueries.available(tenant))
	},

	head: () => ({ meta: [{ title: 'Overview | Air | BitLyft AIR®' }] }),

	component: RouteComponent,
	pendingComponent: OverviewSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const search = Route.useSearch()
	const navigate = Route.useNavigate()

	return (
		<OverviewPage
			search={search}
			onChange={(next) => {
				rememberSelection(next)

				return navigate({
					search: next,
					// Refining one custom range into another REPLACES; every other
					// move pushes. Picking a range is two clicks, and the first of
					// them is a complete (one-day) window in its own right — so a
					// pause between them commits an interim range nobody chose.
					// Replacing collapses those into one entry, which keeps the back
					// button a list of windows the user actually settled on while
					// still stepping back out to the preset they came from.
					replace: search.preset === 'custom' && next.preset === 'custom',
				})
			}}
		/>
	)
}
